import { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useStatsigClient } from '@statsig/react-bindings';

import classNames from 'classnames';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { nanoid } from 'nanoid';

import {
  createImageObject,
  createQuestion,
  FOLDER_ID_CUSTOM,
  IMAGE_UPLOAD_MAX_FILE_SIZE,
  QUESTION_TYPES,
  QUESTION_TYPES_WITH_OPTIONS,
  SURVEY_TYPES,
  updateQuestion,
  uploadImageToS3,
  uploadImageToS3ViaPost,
} from '@api/instant_survey';
import { GATES } from '@constants';
import { isEUDomain } from '@helpers';
import {
  Announcement,
  Button,
  ButtonText,
  Checkbox,
  ColumnsIcon,
  ErrorText,
  Fieldset,
  IconButton,
  Input,
  Layout,
  ModalV2 as Modal,
  OverlayTriggerTooltip,
  PencilIcon,
  PlusIcon,
  RichTextInput,
  SerifXIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from '@utilities';

import Answer from './components/Answer';
import AnswersFormat from './components/AnswersFormat';
import EditableText from './components/EditableText';
import GridAnnouncement from './components/GridAnnouncement';
import MaxAnswers from './components/MaxAnswers';
import MaxDiffAnnouncement from './components/MaxDiffAnnouncement';
import MaxTotal from './components/MaxTotal';
import NumberOfSets from './components/NumberOfSets';
import OptionsPerSet from './components/OptionsPerSet';
import Sidebar from './components/Sidebar';
import SubQuestion from './components/SubQuestion';
import ValuesBetween from './components/ValuesBetween';
import VideoSurveyInstructions from './components/VideoSurveyInstructions';

import {
  ANSWER_MAX_LENGTH,
  availableQuestionVariables,
  DEFAULT_QUESTION_PROPS_BY_TYPE,
  newItemAvailableQuestionVariables,
  NONE_OF_THE_ABOVE,
  NONE_OF_THE_ABOVE_EU,
  OTHER_PLEASE_SPECIFY,
  OTHER_PLEASE_SPECIFY_ALTERNATES,
  OTHER_PLEASE_SPECIFY_EU,
  OTHER_PLEASE_SPECIFY_ALTERNATES_EU,
  QUESTION_CONT_SUM_MAX_OPTIONS,
  QUESTION_GRID_MAX_COLUMNS,
  QUESTION_GRID_MAX_ROWS,
  QUESTION_MAX_DIFF_ANSWER_LENGTH,
  QUESTION_MAX_DIFF_LABEL_LENGTH,
  QUESTION_MAX_DIFF_MAX_OPTIONS,
  QUESTION_MAX_DIFF_MIN_OPTIONS,
  QUESTION_MAX_LENGTH,
  QUESTION_RANK_MAX_OPTIONS,
  QUESTION_TURF_MAX_OPTIONS,
  VALIDATION_MESSAGES,
} from './utilities/constants';

import { SurveyContext } from '../../../../utilities/context';
import { getQuestionCost } from '../../../../utilities/helpers';
import { prepExistingQuestion } from './utilities/helpers';

import styles from './_index.module.scss';
const ModalInstantSurveyQuestion = ({
  isDuplicate,
  onClose,
  onCreate,
  onUpdate,
  originalQuestion,
  pipingQuestionsMap,
}) => {
  const [deletedAnswers, setDeletedAnswers] = useState([]);
  const [deletedSubQuestions, setDeletedSubQuestions] = useState([]);
  const [errorSubmitting, setErrorSubmitting] = useState(null);
  const [hasImageError, setHasImageError] = useState({ answers: [] });
  const [isImageLoading, setIsImageLoading] = useState({ answers: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subQuestionDeleteError, setSubQuestionDeleteError] = useState(null);

  const { activeQuestions, currentQuestionCount, instantSurveyType, maxQuestions, survey } =
    useContext(SurveyContext);

  const { client } = useStatsigClient();
  const canContinuousSum = client.checkGate(GATES.QUESTION_TYPE_CONT_SUM);
  const canMaxDiff = client.checkGate(GATES.QUESTION_TYPE_MAX_DIFF);
  const canNumeric = client.checkGate(GATES.QUESTION_TYPE_NUMERIC);
  const canPipingResponses = client.checkGate(GATES.PIPING_RESPONSES);
  const canVideo = client.checkGate(GATES.QUESTION_TYPE_VIDEO);
  const useImageUploadValidation = client.checkGate(GATES.IMAGE_UPLOAD_VALIDATION);
  const isVideoSurvey = instantSurveyType.id === SURVEY_TYPES.VIDEO;

  const availableQuestionTypes = [
    QUESTION_TYPES.CHECKBOX,
    QUESTION_TYPES.RADIO,
    QUESTION_TYPES.ESSAY,
    QUESTION_TYPES.INSTRUCTIONS,
    QUESTION_TYPES['RANK-DRAGDROP'],
    QUESTION_TYPES['TABLE-RADIO'],
    QUESTION_TYPES.TURF,
    ...(canContinuousSum ? [QUESTION_TYPES.CONTSUM] : []),
    ...(canMaxDiff ? [QUESTION_TYPES.MAX_DIFF] : []),
    ...(canNumeric ? [QUESTION_TYPES.NUMERIC] : []),
    ...(canVideo ? [QUESTION_TYPES.VIDEO] : []),
  ];

  const [question, setQuestion] = useState(
    originalQuestion
      ? prepExistingQuestion({ availableQuestionTypes, isDuplicate, question: originalQuestion })
      : isVideoSurvey
        ? {
            answers: [{ id: nanoid(), text: '' }],
            image: null,
            noneAnswer: null,
            otherAnswer: null,
            properties: {},
            showConceptImage: false,
            subQuestions: [],
            text: '',
            type: QUESTION_TYPES.VIDEO.value,
          }
        : {
            answers: Array.from(new Array(3), () => ({
              id: nanoid(),
              text: '',
            })),
            image: null,
            noneAnswer: null,
            otherAnswer: null,
            properties: { ...DEFAULT_QUESTION_PROPS_BY_TYPE[QUESTION_TYPES.CHECKBOX.value] },
            showConceptImage: false,
            subQuestions: [],
            text: '',
            type: QUESTION_TYPES.CHECKBOX.value,
          }
  );

  const currentQuestionType = useMemo(() => {
    return (
      Object.values(QUESTION_TYPES).find(({ value }) => value === question.type) ||
      QUESTION_TYPES.ESSAY
    );
  }, [question.type]);

  const isCheckbox = question.type === QUESTION_TYPES.CHECKBOX.value;
  const isContSum = question.type === QUESTION_TYPES.CONTSUM.value;
  const isEditing = originalQuestion && !isDuplicate;
  const isGridType = question.type === QUESTION_TYPES['TABLE-RADIO'].value;
  const isInstructions = question.type === QUESTION_TYPES.INSTRUCTIONS.value;
  const isMaxDiff = question.type === QUESTION_TYPES.MAX_DIFF.value;
  const isNumeric = question.type === QUESTION_TYPES.NUMERIC.value;
  const isRadio = question.type === QUESTION_TYPES.RADIO.value;
  const isRankType = question.type === QUESTION_TYPES['RANK-DRAGDROP'].value;
  const isTurf = question.type === QUESTION_TYPES.TURF.value;
  const isVideo = question.type === QUESTION_TYPES.VIDEO.value;

  const canAddNone = (isCheckbox || isRadio || isTurf) && !question.noneAnswer;
  const canAddOther = (isCheckbox || isRadio || isTurf) && !question.otherAnswer;
  const canPipingResponseTypes = [
    QUESTION_TYPES['TABLE-RADIO'].value,
    QUESTION_TYPES.NUMERIC.value,
    QUESTION_TYPES.RADIO.value,
  ];
  const canRandomizeOptions = [
    QUESTION_TYPES.CHECKBOX.value,
    QUESTION_TYPES.CONTSUM.value,
    QUESTION_TYPES.RADIO.value,
    QUESTION_TYPES['RANK-DRAGDROP'].value,
    QUESTION_TYPES.TURF.value,
  ].includes(question.type);

  const noneOfTheAbove = isEUDomain() ? NONE_OF_THE_ABOVE_EU : NONE_OF_THE_ABOVE;
  const otherAlternates = isEUDomain()
    ? OTHER_PLEASE_SPECIFY_ALTERNATES_EU
    : OTHER_PLEASE_SPECIFY_ALTERNATES;
  const otherPleaseSpecify = isEUDomain() ? OTHER_PLEASE_SPECIFY_EU : OTHER_PLEASE_SPECIFY;

  const hasConceptImage =
    Array.isArray(survey?.groups) && survey.groups.some((group) => group?.conceptImage != null);
  const hasConceptName =
    Array.isArray(survey?.groups) && survey.groups.some((group) => group?.conceptName != null);

  useEffect(() => {
    if (isGridType) {
      setQuestion((prev) => ({
        ...prev,
        ...(prev.subQuestions.length === 0 && {
          subQuestions: Array.from(new Array(3), () => ({
            id: nanoid(),
            text: '',
            type: 'RADIO',
          })),
        }),
      }));
    } else if (!isGridType && question.subQuestions.length) {
      setDeletedSubQuestions((prev) => [
        ...prev,
        ...question.subQuestions.filter(({ id }) => typeof id === 'number'),
      ]);
      setQuestion((prev) => ({ ...prev, subQuestions: [] }));
    }

    if (!isCheckbox && !isRadio && !isTurf) {
      deleteNoneAnswer();
      deleteOtherAnswer();
    }
  }, [question.type]);

  const addAnswer = () => {
    setQuestion({ ...question, answers: [...question.answers, { id: nanoid(), text: '' }] });
  };

  const addSubQuestion = () => {
    setQuestion({
      ...question,
      subQuestions: [...question.subQuestions, { id: nanoid(), text: '', type: 'RADIO' }],
    });
  };

  const addNoneAnswer = () => {
    setQuestion((question) => ({
      ...question,
      noneAnswer: {
        id: nanoid(),
        isAnchored: true,
        isExclusive: true,
        isNoneOfTheAbove: true,
        text: noneOfTheAbove,
      },
    }));
  };

  const addOtherAnswer = () => {
    setQuestion((question) => ({
      ...question,
      otherAnswer: {
        id: nanoid(),
        isAnchored: true,
        isOther: true,
        text: otherPleaseSpecify,
      },
    }));
  };

  const deleteAnswer = (index) => {
    const newAnswers = [...question.answers];
    const deletedAnswer = newAnswers.splice(index, 1)[0];
    if (typeof deletedAnswer.id === 'number') {
      setDeletedAnswers((prev) => [...prev, { ...deletedAnswer }]);
    }
    setQuestion((prev) => ({ ...prev, answers: [...newAnswers] }));
    updateMaxAnswersAfterDelete();
  };

  const deleteOtherAnswer = () => {
    if (typeof question.otherAnswer?.id === 'number') {
      setDeletedAnswers((prev) => [...prev, { ...question.otherAnswer }]);
    }
    setQuestion((prev) => ({ ...prev, otherAnswer: null }));
    updateMaxAnswersAfterDelete();
  };

  const deleteNoneAnswer = () => {
    if (typeof question.noneAnswer?.id === 'number') {
      setDeletedAnswers((prev) => [...prev, { ...question.noneAnswer }]);
    }
    setQuestion((prev) => ({ ...prev, noneAnswer: null }));
    updateMaxAnswersAfterDelete();
  };

  const deleteSubQuestion = (index) => {
    const children = pipingQuestionsMap.childrenByParent[question.id] || [];
    const hasChildrenAffected = children.some(({ questionText }) => {
      const regex = new RegExp(/\{answer from q(\d+).(\d+)\}/g);
      // Extract the question number from the piping text
      const match = regex.exec(questionText);
      return match && match[1] - 1 === currentQuestionIndex;
    });

    if (hasChildrenAffected) {
      setSubQuestionDeleteError(
        'This sub-question can’t be deleted because this grid question is used as a piping source for another question. Remove the piping reference first, then delete this sub-question.'
      );
      // Auto-clear the error after 5 seconds
      setTimeout(() => {
        setSubQuestionDeleteError(null);
      }, 5000);
      return;
    }

    setSubQuestionDeleteError(null);
    const remainingSubQuestions = [...question.subQuestions];

    const deletedSubQuestion = remainingSubQuestions.splice(index, 1)[0];

    if (typeof deletedSubQuestion.id === 'number') {
      setDeletedSubQuestions([...deletedSubQuestions, { ...deletedSubQuestion }]);
    }
    setQuestion((prev) => ({ ...prev, subQuestions: [...remainingSubQuestions] }));
  };

  const getNewOptionTooltip = () => {
    if (isContSum && question.answers.length >= QUESTION_CONT_SUM_MAX_OPTIONS)
      return VALIDATION_MESSAGES.CONTSUM_MAX_OPTIONS_ERROR;
    if (isMaxDiff && question.answers.length >= QUESTION_MAX_DIFF_MAX_OPTIONS)
      return VALIDATION_MESSAGES.MAX_DIFF_MAX_OPTIONS_ERROR;
    if (isRankType && question.answers.length >= QUESTION_RANK_MAX_OPTIONS)
      return VALIDATION_MESSAGES.RANK_MAX_OPTIONS_ERROR;
    return null;
  };

  const getSaveButtonTooltip = () => {
    if (!question.text.trim()) return VALIDATION_MESSAGES.QUESTION_TEXT_REQUIRED;
    // pasting text into <MentionsInput /> can exceed its maxLength, these checks are still necessary
    // https://github.com/signavio/react-mentions/issues/616
    if (question.text.length > QUESTION_MAX_LENGTH)
      return VALIDATION_MESSAGES.QUESTION_MAX_LENGTH_ERROR;

    if (isContSum) {
      if (question.answers.length > QUESTION_CONT_SUM_MAX_OPTIONS)
        return VALIDATION_MESSAGES.CONTSUM_MAX_OPTIONS_ERROR;
      if (question.noneAnswer || question.otherAnswer)
        return `${QUESTION_TYPES.CONTSUM.name} cannot have ${otherPleaseSpecify} or ${noneOfTheAbove} options.`;
    }

    if (isTurf) {
      if (question.answers.length > QUESTION_TURF_MAX_OPTIONS)
        return VALIDATION_MESSAGES.TURF_MAX_OPTIONS_ERROR;
    }

    if (isGridType) {
      if (question.answers.length === 0) return VALIDATION_MESSAGES.GRID_COLUMNS_REQUIRED;
      if (question.answers.length > QUESTION_GRID_MAX_COLUMNS)
        return VALIDATION_MESSAGES.GRID_MAX_COLUMNS_ERROR;
      if (question.answers.some(({ text }) => !text.trim()))
        return VALIDATION_MESSAGES.GRID_COLUMNS_BLANK_ERROR;

      if (question.subQuestions.length === 0) return VALIDATION_MESSAGES.GRID_ROWS_REQUIRED;
      if (question.subQuestions.length > QUESTION_GRID_MAX_ROWS)
        return VALIDATION_MESSAGES.GRID_MAX_ROWS_ERROR;
      if (question.subQuestions.some(({ text }) => !text.trim()))
        return VALIDATION_MESSAGES.GRID_ROWS_BLANK_ERROR;
      if (question.subQuestions.some(({ text }) => text.length > ANSWER_MAX_LENGTH))
        return VALIDATION_MESSAGES.GRID_ROW_LENGTH_ERROR;
    }

    if (isMaxDiff) {
      if (!question.properties.bestLabel?.trim())
        return VALIDATION_MESSAGES.MAX_DIFF_BEST_LABEL_REQUIRED;
      if (question.properties.bestLabel?.length > QUESTION_MAX_DIFF_LABEL_LENGTH)
        return VALIDATION_MESSAGES.MAX_DIFF_BEST_LABEL_LENGTH_ERROR;
      if (!question.properties.worstLabel?.trim())
        return VALIDATION_MESSAGES.MAX_DIFF_WORST_LABEL_REQUIRED;
      if (question.properties.worstLabel?.length > QUESTION_MAX_DIFF_LABEL_LENGTH)
        return VALIDATION_MESSAGES.MAX_DIFF_WORST_LABEL_LENGTH_ERROR;
      if (question.answers.length < QUESTION_MAX_DIFF_MIN_OPTIONS)
        return VALIDATION_MESSAGES.MAX_DIFF_MIN_OPTIONS_ERROR;
      if (question.answers.length > QUESTION_MAX_DIFF_MAX_OPTIONS)
        return VALIDATION_MESSAGES.MAX_DIFF_MAX_OPTIONS_ERROR;
      if (question.answers.some(({ text }) => text.length > QUESTION_MAX_DIFF_ANSWER_LENGTH))
        return VALIDATION_MESSAGES.MAX_DIFF_ANSWER_LENGTH_ERROR;
    }

    if (isRankType) {
      if (question.answers.length > QUESTION_RANK_MAX_OPTIONS)
        return VALIDATION_MESSAGES.RANK_MAX_OPTIONS_ERROR;
      if (question.noneAnswer || question.otherAnswer)
        return `${QUESTION_TYPES['RANK-DRAGDROP'].name} cannot have ${otherPleaseSpecify} or ${noneOfTheAbove} options.`;
    }

    if (isCheckbox || isContSum || isMaxDiff || isGridType || isRadio || isRankType || isTurf) {
      if (question.answers.length === 0) return VALIDATION_MESSAGES.ANSWERS_REQUIRED;
      if (question.answers.some(({ text }) => !text.trim())) {
        return VALIDATION_MESSAGES.ANSWER_BLANK_ERROR;
      }
      if (question.answers.some(({ text }) => text.length > ANSWER_MAX_LENGTH))
        return VALIDATION_MESSAGES.ANSWER_LENGTH_ERROR;
    }

    if (
      question.properties.forcePercent &&
      (question.properties.maxNumber > 100 || question.properties.maxTotal > 100)
    )
      return VALIDATION_MESSAGES.MAX_PERCENTAGE_ERROR;
    if (question.properties.maxNumber > question.properties.maxTotal)
      return VALIDATION_MESSAGES.MAX_VALUE_MAX_TOTAL_ERROR;

    const currentQuestionCost = getQuestionCost(question);
    const originalQuestionCost = originalQuestion ? getQuestionCost(originalQuestion) : 0;
    if (
      currentQuestionCost + currentQuestionCount - (isEditing ? originalQuestionCost : 0) >
      maxQuestions
    ) {
      return VALIDATION_MESSAGES.QUESTION_COST_ERROR;
    }

    return null;
  };

  const getAnswerInputError = ({ answer, index }) => {
    if (isContSum && index >= QUESTION_CONT_SUM_MAX_OPTIONS)
      return VALIDATION_MESSAGES.CONTSUM_MAX_OPTIONS_ERROR;
    if (isMaxDiff && answer.text?.length > QUESTION_MAX_DIFF_ANSWER_LENGTH)
      return VALIDATION_MESSAGES.MAX_DIFF_ANSWER_LENGTH_ERROR;
    if (isMaxDiff && index >= QUESTION_MAX_DIFF_MAX_OPTIONS)
      return VALIDATION_MESSAGES.MAX_DIFF_MAX_OPTIONS_ERROR;
    if (isRankType && index >= QUESTION_RANK_MAX_OPTIONS)
      return VALIDATION_MESSAGES.RANK_MAX_OPTIONS_ERROR;
    if (answer.text?.length > ANSWER_MAX_LENGTH) return VALIDATION_MESSAGES.ANSWER_LENGTH_ERROR;
    if (!answer.text?.trim() && answer.cdnUrl) return VALIDATION_MESSAGES.ANSWER_BLANK_ERROR;
    return null;
  };

  const getSubQuestionError = ({ index, subQuestion }) => {
    if (isGridType && index >= QUESTION_GRID_MAX_ROWS)
      return VALIDATION_MESSAGES.GRID_MAX_ROWS_ERROR;
    if (subQuestion.text?.length > ANSWER_MAX_LENGTH)
      return VALIDATION_MESSAGES.GRID_ROW_LENGTH_ERROR;
    return null;
  };

  const handleSelectQuestionType = (type) => {
    if (type === question.type) return;

    setQuestion((prev) => ({
      ...prev,
      properties: {
        ...(DEFAULT_QUESTION_PROPS_BY_TYPE[type] || {}),
        ...(canRandomizeOptions && { randomizeOptions: prev.properties.randomizeOptions ?? false }),
      },
      type,
    }));
  };

  const onAnswerDragEnd = (result) => {
    if (!result?.destination) {
      return;
    }
    const answers = [...question.answers];
    const [removed] = answers.splice(result?.source?.index, 1);
    answers.splice(result?.destination?.index, 0, removed);
    setQuestion({ ...question, answers });
  };

  const onAnswerPaste = (event, value, index) => {
    const lines = value
      .split(/[\n\r]+/) // split on newlines and returns
      .filter((n) => n); // filter removes empty string in cases of repeated line breaks
    if (lines.length <= 1) return;

    event.preventDefault();
    const values = lines
      .map((text) => text.trim().replace(/^[\s•*\-□]+/, '')) // trim whitespace and remove bullet points, checkboxes, and dashes
      .filter((text) => {
        if (otherAlternates.includes(text.toLowerCase())) {
          if (canAddOther) addOtherAnswer();
          return false;
        } else if (text.toLowerCase() === noneOfTheAbove.toLowerCase()) {
          if (canAddNone) addNoneAnswer();
          return false;
        }
        return true;
      })
      .map((text) => ({
        id: nanoid(),
        text,
      }));

    const deletedItems = question.answers.splice(index, values.length, ...values);
    setDeletedAnswers([
      ...deletedAnswers,
      ...deletedItems.filter(({ id }) => typeof id === 'number'),
    ]);
    setQuestion((question) => ({ ...question })); // updates the answers prop after the above splice()
  };

  const onImageUpload = async (file, index) => {
    setImageError(false, index);
    setImageLoading(true, index);

    try {
      const imageObject = await createImageObject({ objectType: file.type.substring(6) });
      if (!imageObject.url.startsWith('http://s3mock')) {
        if (useImageUploadValidation && imageObject.fields) {
          await uploadImageToS3ViaPost({
            fields: imageObject.fields,
            file,
            url: imageObject.url,
          });
        } else {
          await uploadImageToS3({ file, url: imageObject.url });
        }
      }
      setUploadedImage(imageObject, index);
      setImageLoading(false, index);
    } catch (error) {
      setImageError(true, index);
      setImageLoading(false, index);
      throw error; // passes error back to RichTextInput handler
    }
  };

  const onSave = async () => {
    setErrorSubmitting(null);
    setIsSubmitting(true);

    const {
      image,
      noneAnswer,
      otherAnswer,
      properties,
      showConceptImage,
      subQuestions,
      text,
      type,
    } = question;
    let answers = [...question.answers];

    if (otherAnswer) answers.push(otherAnswer);
    if (noneAnswer) answers.push(noneAnswer);

    if (
      !QUESTION_TYPES_WITH_OPTIONS.includes(type) &&
      type !== QUESTION_TYPES['TABLE-RADIO'].value
    ) {
      // mark any existing answers as _delete: true
      answers = answers
        .filter(({ id }) => typeof id === 'number')
        .map((answer) => ({ _delete: true, ...answer }));
    }

    const controller = new AbortController();
    try {
      let data;
      if (question.id) {
        data = await updateQuestion({
          question: {
            answers: [
              ...answers,
              ...deletedAnswers.map((answer) => ({ _delete: true, ...answer })),
            ],
            id: question.id,
            image,
            properties,
            showConceptImage,
            subQuestions: [
              ...subQuestions,
              ...deletedSubQuestions.map((subQuestion) => ({
                _delete: true,
                ...subQuestion,
              })),
            ],
            text,
            type,
          },
          signal: controller.signal,
        });
      } else {
        data = await createQuestion({
          question: {
            answers,
            folder: FOLDER_ID_CUSTOM,
            image,
            instantSurveyTypes: [instantSurveyType.id],
            properties,
            showConceptImage,
            subQuestions,
            text,
            type,
          },
          signal: controller.signal,
        });
      }
      if (isEditing) return onUpdate(data);
      return onCreate(data);
    } catch (error) {
      if (!controller.signal.aborted) {
        setErrorSubmitting(error);
        setIsSubmitting(false);
      }
    }
  };

  const onSubQuestionDragEnd = (result) => {
    if (!result?.destination) return;

    const subQuestions = [...question.subQuestions];
    const [removed] = subQuestions.splice(result?.source?.index, 1);
    subQuestions.splice(result?.destination?.index, 0, removed);
    setQuestion((prev) => ({ ...prev, subQuestions }));
  };

  const setImageError = (value, index) => {
    const updatedImageError = { ...hasImageError };
    if (index !== undefined) {
      updatedImageError.answers[index] = value;
    } else {
      updatedImageError.question = value;
    }
    setHasImageError(updatedImageError);
  };

  const setImageLoading = (value, index) => {
    const updatedImageLoading = { ...isImageLoading };
    if (index !== undefined) {
      updatedImageLoading.answers[index] = value;
    } else {
      updatedImageLoading.question = value;
    }
    setIsImageLoading(updatedImageLoading);
  };

  const setUploadedImage = (value, index) => {
    if (index !== undefined) {
      const answerId = question.answers[index].id;
      updateAnswer(answerId, { image: value });
    } else {
      setQuestion({ ...question, image: value });
    }
  };

  const updateAnswer = (answerId, changes) => {
    const answer = question.answers.find(({ id }) => answerId === id);
    Object.assign(answer, changes);
    setQuestion({ ...question, answers: question.answers });
  };

  const updateMaxAnswersAfterDelete = () => {
    setQuestion((prev) => {
      const maxAnswers = Math.min(
        prev.properties.maxAnswers,
        prev.answers.length + (prev.otherAnswer ? 1 : 0) + (prev.noneAnswer ? 1 : 0)
      );

      return {
        ...prev,
        properties: {
          ...prev.properties,
          // set maxAnswers to undefined if all answers are deleted
          maxAnswers: maxAnswers || undefined,
        },
      };
    });
  };

  const updateQuestionProperty = ({ key, value }) => {
    setQuestion((prev) => ({
      ...prev,
      properties: {
        ...prev.properties,
        [key]: value,
      },
    }));
  };

  const newOptionTooltip = getNewOptionTooltip();
  const saveButtonTooltip = getSaveButtonTooltip();
  const actionButtons = [
    <Button
      data-testid="modal-cancel-button"
      key="modal-cancel-button"
      onClick={onClose}
      text="Cancel"
      variant="secondary"
    />,
    <OverlayTriggerTooltip content={saveButtonTooltip} key="modal-confirm-button" placement="top">
      <Button
        data-testid="modal-confirm-button"
        isDisabled={Boolean(saveButtonTooltip)}
        isLoading={isSubmitting}
        onClick={onSave}
        text={isEditing ? 'Update' : 'Create & Add'}
      />
    </OverlayTriggerTooltip>,
  ];

  const currentQuestionIndex = activeQuestions.findIndex(({ id }) => id === question.id);

  const surveyQuestionsForPiping = useMemo(() => {
    let questionIndex = 1; // cumulative question cost tracking
    return activeQuestions
      .slice(0, currentQuestionIndex === -1 ? undefined : currentQuestionIndex)
      .flatMap((question) => {
        const result = question.subQuestions?.length
          ? question.subQuestions.map((subQ, subIndex) => ({
              display: `Question ${questionIndex}.${subIndex + 1} - ${subQ.text}`,
              id: `answer from q${questionIndex}.${subIndex + 1}`,
              type: question.type,
            }))
          : {
              display: `Question ${questionIndex}: ${question.text}`,
              id: `answer from q${questionIndex}`,
              type: question.type,
            };

        questionIndex += getQuestionCost(question);
        return result;
      })
      .filter(({ type }) => canPipingResponseTypes.includes(type));
  }, [activeQuestions, canPipingResponseTypes, currentQuestionIndex]);

  const mergedQuestionVariables =
    instantSurveyType?.id === SURVEY_TYPES.NEW_ITEM
      ? [...availableQuestionVariables, ...newItemAvailableQuestionVariables]
      : availableQuestionVariables;

  const variablesMention = {
    data: mergedQuestionVariables.map((variable) => ({
      display: variable,
      id: variable,
    })),
    displayTransform: (_, display) => `[${display.toLowerCase()}]`,
    icon: <SerifXIcon />,
    key: 'variables',
    label: 'Add a Variable',
    markup: '[__display__]',
    trigger: '[',
  };
  const pipingMention = {
    data: surveyQuestionsForPiping,
    displayTransform: (id) => `{${id}}`,
    icon: <PencilIcon />,
    key: 'questions',
    label: 'Add Previous Answer',
    markup: '{__id__}',
    trigger: '{',
  };

  const mentions =
    canPipingResponses && surveyQuestionsForPiping.length
      ? [variablesMention, pipingMention]
      : [variablesMention];

  const suggestions = {
    description: 'Use our dynamic variables to help with recalls for your surveys.',
    helpUrl: instantSurveyType.helpUrl,
    title: 'Search for Variables',
  };

  return (
    <Modal
      buttons={actionButtons}
      className={styles['modal-instant-survey-question']}
      isActive={true}
      onClose={onClose}
      sidebar={
        <Sidebar
          isVideoSurvey={isVideoSurvey}
          onSelectQuestionType={handleSelectQuestionType}
          question={question}
        />
      }
      size="large"
      title={`${isEditing ? 'Edit' : 'Create'} Survey Question`}
    >
      <Layout.Flex.Column>
        {errorSubmitting && (
          <Announcement
            text={
              errorSubmitting?.response?.data?.error ||
              `There was an error ${question.id ? 'updating' : 'creating'} your question.`
            }
            variant="error"
          />
        )}
        <Layout.Flex.Column
          className={classNames(styles['modal-instant-survey-question-text-wrapper'], {
            [styles['has-options']]:
              QUESTION_TYPES_WITH_OPTIONS.includes(question.type) || isGridType,
          })}
        >
          <RichTextInput
            autoFocus={question.id ? false : true}
            error={
              question.text.length > QUESTION_MAX_LENGTH
                ? VALIDATION_MESSAGES.QUESTION_MAX_LENGTH_ERROR
                : null
            }
            hasConceptImage={hasConceptImage}
            hasConceptName={hasConceptName}
            hasImageError={hasImageError.question}
            id="question-text-rich-input"
            imageUrl={question.image?.cdnUrl}
            isExpanded
            isImageLoading={isImageLoading.question}
            isSingleLine={false}
            label={
              <div className={styles['modal-instant-survey-question-text-label']}>
                {currentQuestionType.icon}
                {currentQuestionType.name}
                {!isInstructions && ' Question'}
              </div>
            }
            maxImageSize={IMAGE_UPLOAD_MAX_FILE_SIZE}
            maxLength={QUESTION_MAX_LENGTH}
            mentions={mentions}
            onChange={(text) => setQuestion({ ...question, text })}
            onImageRemove={setUploadedImage}
            onImageUpload={onImageUpload}
            placeholder="Enter question"
            setHasImageError={setImageError}
            setShouldShowConceptImage={() => {
              setQuestion((prev) => ({
                ...prev,
                showConceptImage: !prev.showConceptImage,
              }));
            }}
            shouldShowConceptImage={question.showConceptImage}
            suggestions={suggestions}
            value={question.text}
          />
          <p className={styles['modal-instant-survey-question-type-description']}>
            {currentQuestionType.description}
          </p>
        </Layout.Flex.Column>

        {isMaxDiff && <MaxDiffAnnouncement question={question} />}

        {isNumeric && (
          <Fieldset legend={`Answer Options`}>
            <Layout.Flex className={styles['modal-instant-survey-question-properties']}>
              <AnswersFormat question={question} setQuestion={setQuestion} />
              <ValuesBetween question={question} setQuestion={setQuestion} />
            </Layout.Flex>
          </Fieldset>
        )}

        {QUESTION_TYPES_WITH_OPTIONS.includes(question.type) && (
          <Layout.Flex.Column gap="small">
            <Fieldset
              legend={`Answer Options (${isMaxDiff ? QUESTION_MAX_DIFF_ANSWER_LENGTH : ANSWER_MAX_LENGTH} characters)`}
            >
              <Layout.Flex className={styles['modal-instant-survey-question-properties']}>
                {canRandomizeOptions && (
                  <div>
                    <Checkbox
                      isChecked={question.properties.randomizeOptions}
                      label="Randomize order"
                      onChange={() =>
                        setQuestion({
                          ...question,
                          properties: {
                            ...question.properties,
                            randomizeOptions: !question.properties.randomizeOptions,
                          },
                        })
                      }
                      value={question.properties.randomizeOptions}
                    />
                  </div>
                )}
                {(isCheckbox || isTurf) && (
                  <>
                    <span className={styles['modal-instant-survey-question-properties-divider']} />
                    <MaxAnswers
                      question={question}
                      setMaxAnswers={(value) =>
                        updateQuestionProperty({ key: 'maxAnswers', value })
                      }
                    />
                  </>
                )}
                {isContSum && (
                  <>
                    <span className={styles['modal-instant-survey-question-properties-divider']} />
                    <AnswersFormat question={question} setQuestion={setQuestion} />
                    <ValuesBetween question={question} setQuestion={setQuestion} />
                    <MaxTotal question={question} setQuestion={setQuestion} />
                  </>
                )}
                {isMaxDiff && (
                  <>
                    <div className={styles['modal-instant-survey-question-column-header']}>
                      <OverlayTriggerTooltip content="Best Label">
                        <ThumbsUpIcon />
                      </OverlayTriggerTooltip>
                      <EditableText
                        aria-label="Best Label"
                        error={
                          question.properties.bestLabel?.length > QUESTION_MAX_DIFF_LABEL_LENGTH
                            ? VALIDATION_MESSAGES.MAX_DIFF_BEST_LABEL_LENGTH_ERROR
                            : null
                        }
                        maxLength={QUESTION_MAX_DIFF_LABEL_LENGTH}
                        onChange={(value) => updateQuestionProperty({ key: 'bestLabel', value })}
                        value={question.properties.bestLabel}
                      />
                    </div>
                    <div className={styles['modal-instant-survey-question-column-header']}>
                      <OverlayTriggerTooltip content="Worst Label">
                        <ThumbsDownIcon />
                      </OverlayTriggerTooltip>
                      <EditableText
                        aria-label="Worst Label"
                        error={
                          question.properties.worstLabel?.length > QUESTION_MAX_DIFF_LABEL_LENGTH
                            ? VALIDATION_MESSAGES.MAX_DIFF_WORST_LABEL_LENGTH_ERROR
                            : null
                        }
                        maxLength={QUESTION_MAX_DIFF_LABEL_LENGTH}
                        onChange={(value) => updateQuestionProperty({ key: 'worstLabel', value })}
                        value={question.properties.worstLabel}
                      />
                    </div>
                    <span className={styles['modal-instant-survey-question-properties-divider']} />
                    <OptionsPerSet
                      question={question}
                      setOptionsPerSet={(value) =>
                        updateQuestionProperty({ key: 'optionsPerSet', value })
                      }
                    />
                    <span className={styles['modal-instant-survey-question-properties-divider']} />
                    <NumberOfSets
                      question={question}
                      setNumberOfSets={(value) =>
                        updateQuestionProperty({ key: 'numberOfSets', value })
                      }
                    />
                  </>
                )}
              </Layout.Flex>
            </Fieldset>

            <DragDropContext onDragEnd={onAnswerDragEnd}>
              <Droppable droppableId="modalSurveyQuestionAnswers">
                {(provided) => (
                  <ul
                    data-testid="answer-list"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {question.answers.map((answer, index) => {
                      return (
                        <Draggable
                          draggableId={`answer-${answer.id}`} // requires a string
                          index={index}
                          isDragDisabled={
                            question.answers.length === 1 ||
                            question.properties.randomizeOptions ||
                            isMaxDiff
                          }
                          key={answer.id}
                        >
                          {(provided) => {
                            return (
                              <li
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                tabIndex="-1"
                              >
                                <Answer
                                  answer={answer}
                                  isDraggable={!isMaxDiff}
                                  onAnchor={() =>
                                    updateAnswer(answer.id, {
                                      isAnchored: !answer.isAnchored,
                                    })
                                  }
                                  onDelete={() => deleteAnswer(index)}
                                  onMakeExclusive={() =>
                                    updateAnswer(answer.id, {
                                      isExclusive: !answer.isExclusive,
                                    })
                                  }
                                  type={question.type}
                                >
                                  <RichTextInput
                                    error={getAnswerInputError({ answer, index })}
                                    hasImageError={hasImageError.answers[index]}
                                    imageUrl={answer.image?.cdnUrl}
                                    isImageLoading={isImageLoading.answers[index]}
                                    maxImageSize={IMAGE_UPLOAD_MAX_FILE_SIZE}
                                    maxLength={
                                      isMaxDiff
                                        ? QUESTION_MAX_DIFF_ANSWER_LENGTH
                                        : ANSWER_MAX_LENGTH
                                    }
                                    mentions={mentions}
                                    onChange={(value) => updateAnswer(answer.id, { text: value })}
                                    onImageRemove={() => setUploadedImage(null, index)}
                                    onImageUpload={(file) => onImageUpload(file, index)}
                                    onPaste={(event, value) => onAnswerPaste(event, value, index)}
                                    placeholder="Enter answer"
                                    setHasImageError={(value) => setImageError(value, index)}
                                    suggestions={suggestions}
                                    value={answer.text}
                                  />
                                </Answer>
                              </li>
                            );
                          }}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>

            {(question.otherAnswer || question.noneAnswer) && (
              <ul
                className={styles['modal-instant-survey-question-options']}
                data-testid="other-none-list"
              >
                {question.otherAnswer && (
                  <li>
                    <Answer
                      answer={question.otherAnswer}
                      onDelete={deleteOtherAnswer}
                      onMakeExclusive={() => {
                        setQuestion({
                          ...question,
                          otherAnswer: {
                            ...question.otherAnswer,
                            isExclusive: !question.otherAnswer.isExclusive,
                          },
                        });
                      }}
                      type={question.type}
                    >
                      <div className={styles['modal-instant-survey-question-other']}>
                        <div>
                          {question.otherAnswer.text}:&nbsp;
                          <em>Panelist's write-in</em>
                        </div>
                        {(isContSum || isRankType) && (
                          <ErrorText>{VALIDATION_MESSAGES.NO_OTHER_PLEASE_SPECIFY}</ErrorText>
                        )}
                      </div>
                    </Answer>
                  </li>
                )}
                {question.noneAnswer && (
                  <li>
                    <Answer
                      answer={question.noneAnswer}
                      onDelete={deleteNoneAnswer}
                      type={question.type}
                    >
                      <div className={styles['modal-instant-survey-question-none']}>
                        <div>{question.noneAnswer.text}</div>
                        {(isContSum || isRankType) && (
                          <ErrorText>{VALIDATION_MESSAGES.NO_NONE_OF_THE_ABOVE}</ErrorText>
                        )}
                      </div>
                    </Answer>
                  </li>
                )}
              </ul>
            )}

            <div className={styles['modal-instant-survey-question-actions']}>
              <PlusIcon />
              <OverlayTriggerTooltip content={newOptionTooltip} placement="top">
                <ButtonText
                  data-testid="add-answer-button"
                  disabled={Boolean(newOptionTooltip)}
                  onClick={addAnswer}
                >
                  <strong>New</strong>&nbsp;Option
                  {canAddOther || canAddNone ? ',' : null}
                </ButtonText>
              </OverlayTriggerTooltip>
              {canAddOther && (
                <ButtonText data-testid="add-other-button" onClick={addOtherAnswer}>
                  <strong>"{otherPleaseSpecify}"</strong>&nbsp;Option
                  {!question.noneAnswer ? ',' : null}
                </ButtonText>
              )}
              {canAddNone && (
                <ButtonText data-testid="add-none-button" onClick={addNoneAnswer}>
                  <strong>"{noneOfTheAbove}"</strong>&nbsp;Option
                </ButtonText>
              )}
            </div>
          </Layout.Flex.Column>
        )}
        {isGridType && (
          <>
            <GridAnnouncement question={question} />
            <Fieldset legend="Column Headers">
              <Layout.Flex className={styles['modal-instant-survey-question-columns']} gap="small">
                {question.answers.map((answer, index) => (
                  <div
                    className={styles['modal-instant-survey-question-column-header']}
                    key={answer.id}
                  >
                    <ColumnsIcon />
                    <EditableText
                      aria-label="Column Header"
                      error={
                        answer.text.length > ANSWER_MAX_LENGTH
                          ? VALIDATION_MESSAGES.GRID_COLUMN_LENGTH_ERROR
                          : null
                      }
                      maxLength={ANSWER_MAX_LENGTH}
                      onChange={(value) => updateAnswer(answer.id, { text: value })}
                      onClear={() => deleteAnswer(index)}
                      value={answer.text}
                    />
                  </div>
                ))}
                <IconButton
                  className={styles['modal-instant-survey-question-column-header-button']}
                  isDisabled={question.answers.length >= QUESTION_GRID_MAX_COLUMNS}
                  onClick={() => addAnswer()}
                  tooltip={
                    question.answers.length < QUESTION_GRID_MAX_COLUMNS ? (
                      'Add Column'
                    ) : (
                      <>
                        <strong>Sorry</strong>. You have reached your column limit. Try removing a
                        column first.
                      </>
                    )
                  }
                >
                  <PlusIcon />
                </IconButton>
              </Layout.Flex>
              {question.answers.length > QUESTION_GRID_MAX_COLUMNS && (
                <ErrorText>{VALIDATION_MESSAGES.GRID_MAX_COLUMNS_ERROR}</ErrorText>
              )}
            </Fieldset>
            <Fieldset legend={`Question Rows (${ANSWER_MAX_LENGTH} characters)`}>
              {subQuestionDeleteError && (
                <Announcement text={subQuestionDeleteError} variant="error" />
              )}
              <DragDropContext onDragEnd={onSubQuestionDragEnd}>
                <Droppable droppableId="modalSurveyQuestionAnswers">
                  {(provided) => (
                    <ul ref={provided.innerRef} {...provided.droppableProps}>
                      {question.subQuestions.map((subQuestion, index) => {
                        return (
                          <Draggable
                            draggableId={`sub_question-${subQuestion.id}`} // requires a string
                            index={index}
                            isDragDisabled={question.subQuestions.length === 1}
                            key={subQuestion.id}
                          >
                            {(provided) => {
                              return (
                                <li
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  tabIndex="-1"
                                >
                                  <SubQuestion
                                    answer={subQuestion}
                                    index={index}
                                    onDelete={() => deleteSubQuestion(index)}
                                  >
                                    <Input
                                      className={
                                        styles['modal-instant-survey-question-sub-question']
                                      }
                                      error={getSubQuestionError({ index, subQuestion })}
                                      maxLength={ANSWER_MAX_LENGTH}
                                      onChange={(value) => {
                                        setQuestion((prev) => {
                                          const updatedQuestion = { ...prev };
                                          updatedQuestion.subQuestions[index].text = value;
                                          return updatedQuestion;
                                        });
                                      }}
                                      value={subQuestion.text}
                                    />
                                  </SubQuestion>
                                </li>
                              );
                            }}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </DragDropContext>
              {question.subQuestions.length > QUESTION_GRID_MAX_ROWS && (
                <ErrorText>{VALIDATION_MESSAGES.GRID_MAX_ROWS_ERROR}</ErrorText>
              )}
            </Fieldset>

            <div className={styles['modal-instant-survey-question-actions']}>
              <PlusIcon />
              <OverlayTriggerTooltip
                content={
                  question.subQuestions.length >= QUESTION_GRID_MAX_ROWS
                    ? `To provide the best experience for our panelists on mobile, this question is limited to ${QUESTION_GRID_MAX_ROWS} rows. If you have more, please create a new question.`
                    : null
                }
                placement="right"
              >
                <ButtonText
                  data-testid="add-answer-button"
                  disabled={question.subQuestions.length >= QUESTION_GRID_MAX_ROWS}
                  onClick={addSubQuestion}
                >
                  <strong>New</strong>&nbsp;Option
                </ButtonText>
              </OverlayTriggerTooltip>
            </div>
          </>
        )}
        {isVideo && <VideoSurveyInstructions />}
      </Layout.Flex.Column>
    </Modal>
  );
};

ModalInstantSurveyQuestion.defaultProps = {
  isDuplicate: false,
};

ModalInstantSurveyQuestion.propTypes = {
  isDuplicate: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  originalQuestion: PropTypes.object,
  pipingQuestionsMap: PropTypes.object.isRequired,
};

export default ModalInstantSurveyQuestion;
